import { useTheme } from "@mui/material";
import Tooltip from "containers/Components/Tooltip";
import { ResizeComponent, SizeProps } from "containers/hooks/ResizeComponent";
import { usePrevious } from "containers/hooks/usePrevious";
import * as d3 from "d3";
import { Category, DataPoint } from "fileParsing/types";
import React, { useMemo, useRef, useState } from "react";
import { v4 } from "uuid";

export type Props = {
  data: Partial<DataPoint>[];
};

type DatedDataPoint = Partial<DataPoint> & { date: Date };

const TinyTimeline: React.FC<Props & SizeProps> = ({ data, size }) => {
  const theme = useTheme();

  const margin = { top: 10, bottom: 30, left: 10, right: 10 };
  const chartHeight = size.height - margin.top - margin.bottom;
  const chartWidth = size.width - margin.left - margin.right;

  const [tooltip, setToolTip] = useState<{
    id: string;
    x: number;
    y: number;
    data: Partial<DataPoint>;
  }>();

  const prevData = usePrevious(data);
  const toggleVal = useRef(0);

  const dataEq =
    data.length === prevData?.length && data.every((v, ix) => prevData[ix] === v)
      ? toggleVal.current
      : (toggleVal.current = toggleVal.current + 1);

  const namespace = useMemo(() => v4(), [dataEq]);

  const datapoints = useMemo(
    () =>
      data
        .filter(
          (datapoint): datapoint is DatedDataPoint =>
            datapoint.date !== undefined && datapoint.date.getTime() > 1000 * 3600 * 24,
        )
        .filter((_, __, arr) => Math.random() < chartWidth / arr.length),
    [dataEq],
  );
  const dates = datapoints.map(({ date }) => date);

  const [min, max] = useMemo(() => {
    return [d3.min(dates, (d) => d.getTime()) || 0, d3.max(dates, (d) => d.getTime()) || 0];
  }, [dates]);

  const ticks = useMemo(() => {
    const xScale = d3.scaleTime().domain([min, max]).range([0, size.width]);

    return xScale.ticks(size.width / 100).map((value) => ({
      value,
      xOffset: xScale(value),
    }));
  }, [dataEq]);

  const axis = {
    x: d3.scaleLinear().domain([min, max]).range([0, chartWidth]),
    y: d3
      .scaleLinear()
      .domain([0, 1])
      .range([margin.top, margin.top + chartHeight]),
  };

  const getDateAsString = React.useCallback(
    (d: Date) => {
      // If the ticks are wide enough we only show the year
      // If they are not as wide we show the months
      if (ticks.length > 1) {
        const difference = ticks[1].value.getTime() - ticks[0].value.getTime();
        if (difference >= 31536000000) {
          return d.getFullYear();
        }
        return `${d.toLocaleString("default", {
          month: "short",
        })} ${d.getFullYear()}`;
      } else return d.toDateString();
    },
    [ticks],
  );

  return (
    <>
      <svg className="tiny-timeline" width={size.width} height={size.height}>
        <g>
          <line
            x1={`${axis.x(min)}%`}
            x2={`${axis.x(max)}%`}
            y1={size.height - margin.bottom}
            y2={size.height - margin.bottom}
            stroke={theme.palette.action.disabled}
          />
          {ticks.map(({ value, xOffset }) => (
            <g key={value.getTime()} transform={`translate(${xOffset + margin.left}, ${size.height - margin.bottom})`}>
              <line y2={6} stroke={theme.palette.action.disabled} />
              <text dominantBaseline="hanging" textAnchor="middle" fill={theme.palette.text.primary} y={5}>
                {getDateAsString(value)}
              </text>
            </g>
          ))}
        </g>
        <g>
          {datapoints.map((d, ix) => {
            const xVal = axis.x(d.date) + margin.left;
            const id = `${namespace}-${ix}`;
            const color = d.category
              ? tooltip?.id === id
                ? theme.palette.action.selected
                : theme.palette.categories[d.category].main
              : theme.palette.primary.main;
            return (
              <line
                key={id}
                x1={xVal}
                x2={xVal}
                y1={0}
                y2={axis.y(1)}
                stroke={color}
                strokeWidth={2}
                onMouseMove={({ clientX, clientY }) => {
                  if (d.category)
                    setToolTip({
                      id,
                      x: clientX,
                      y: clientY,
                      data: d,
                    });
                }}
                onMouseLeave={() => {
                  setToolTip(undefined);
                }}
              />
            );
          })}
        </g>
      </svg>
      <Tooltip
        settings={{
          tooltipWidth: 400,
          margin: 0,
        }}
        visible={tooltip !== undefined}
        texts={{
          title: tooltip?.data.category,
          subtitle: tooltip?.data.subcategory,
          date: tooltip?.data.date?.toDateString(),
          body: tooltip?.data.text,
        }}
        uri={tooltip?.data.uri}
        category={tooltip?.data.category ?? Category.Other}
        position={{
          x: tooltip?.x ?? -1000,
          y: tooltip?.y ?? -1000,
        }}
      />
    </>
  );
};

export default ResizeComponent(TinyTimeline);
